import {Dialog} from '@primer/react'
import {InlineMessage} from '@primer/react/experimental'

export type UnstackDialogProps = {
  errorMessage: string
  isConfirmingUnstack: boolean
  isInactive: boolean
  isLoading: boolean
  onCancelUnstack: () => void
  onConfirmUnstack: () => void
  unstackButtonRef: React.RefObject<HTMLButtonElement | null>
}

export function UnstackDialog({
  unstackButtonRef,
  onCancelUnstack,
  onConfirmUnstack,
  isConfirmingUnstack,
  isInactive,
  isLoading,
  errorMessage,
}: UnstackDialogProps) {
  if (!isConfirmingUnstack) {
    return null
  }

  if (isInactive) {
    return (
      <Dialog title="Unstacking not allowed" returnFocusRef={unstackButtonRef} onClose={onCancelUnstack}>
        One or more pull requests in the stack are queued for merge. Remove from queue and try again.
      </Dialog>
    )
  }

  return (
    <Dialog
      title="Are you sure you want to unstack these pull requests?"
      returnFocusRef={unstackButtonRef}
      onClose={onCancelUnstack}
      footerButtons={[
        {buttonType: 'default', content: 'Cancel', onClick: onCancelUnstack},
        {
          buttonType: 'danger',
          content: 'Unstack',
          onClick: onConfirmUnstack,
          // aria-disabled must be not set when false to avoid broken styling
          'aria-disabled': isLoading ? 'true' : undefined,
          disabled: isLoading,
          loading: isLoading,
        },
      ]}
    >
      All pull requests will be removed from the stack. Their base branches will not be retargeted and their commit
      histories will remain the same.
      {errorMessage && (
        <InlineMessage className="mt-2" variant="critical">
          {errorMessage}
        </InlineMessage>
      )}
      {/* Loading message accessible to screen readers only. See https://primer.style/components/button#button-loading-state */}
      <span className="sr-only" aria-live="polite">
        {isLoading ? 'Unstacking pull requests in progress.' : ''}
      </span>
    </Dialog>
  )
}
